import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { FaSearch } from "react-icons/fa";
import styles from "../ING.module.css";

export default function CustomSelect({ options = [], value = [], placeholder = "All", onChange }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    const selectedValues = useMemo(() => new Set(value.map((v) => v.value)), [value]);
    const allSelected = options.length > 0 && value.length === options.length;

    const filteredOptions = useMemo(() => {
        if (!search) return options;
        const q = search.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, search]);

    const toggleOption = (option) => {
        if (selectedValues.has(option.value)) {
            onChange(value.filter((v) => v.value !== option.value));
        } else {
            onChange([...value, option]);
        }
    };

    const toggleSelectAll = () => {
        onChange(allSelected ? [] : [...options]);
    };

    const label =
        value.length === 0
            ? placeholder
            : value.length === 1
                ? value[0].label
                : `${value.length} selected`;

    return (
        <div className={styles.multiSelect} ref={wrapperRef}>
            <button
                type="button"
                className={styles.multiSelectToggle}
                onClick={() => setOpen((p) => !p)}
            >
                <span className={styles.multiSelectToggleLabel}>{label}</span>
                <ChevronDown
                    size={14}
                    className={`${styles.dateDropdownChevron} ${open ? styles.dateDropdownChevronOpen : ""}`}
                />
            </button>

            {open && (
                <div className={styles.multiSelectMenu}>
                    <div className={styles.multiSelectSearchWrap}>
                        <FaSearch className={styles.multiSelectSearchIcon} size={12} />
                        <input
                            autoFocus
                            className={styles.multiSelectSearch}
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className={styles.multiSelectList}>
                        <label className={`${styles.multiSelectOption} ${styles.multiSelectSelectAll}`}>
                            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                            <span>Select All</span>
                        </label>

                        {filteredOptions.map((option) => (
                            <label key={option.value} className={styles.multiSelectOption}>
                                <input
                                    type="checkbox"
                                    checked={selectedValues.has(option.value)}
                                    onChange={() => toggleOption(option)}
                                />
                                <span>{option.label}</span>
                            </label>
                        ))}

                        {!filteredOptions.length && (
                            <div className={styles.multiSelectEmpty}>No matches</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
